import { useCallback, useEffect, useMemo, useState } from 'react';
import type { BirdEyeBoardCounts, BirdEyeBoardState, BirdEyeDragSource, BirdEyeDropTarget } from './boardTypes';
import { movePatientInBoard } from './movePatient';

export function createInitialBirdEyeBoardState(): BirdEyeBoardState {
  return {
    arriving: [],
    waiting: [],
    walking: [],
    ops: [],
  };
}

export function useBirdEyeBoard(initialData?: BirdEyeBoardState, syncKey?: string) {
  const [state, setState] = useState<BirdEyeBoardState>(
    () => initialData ?? createInitialBirdEyeBoardState(),
  );

  useEffect(() => {
    if (initialData && syncKey != null) {
      setState(initialData);
    }
  }, [syncKey]);

  const movePatient = useCallback((from: BirdEyeDragSource, to: BirdEyeDropTarget) => {
    setState((prev) => movePatientInBoard(prev, from, to));
  }, []);

  /** Drops any local (optimistic) moves and returns to the last server-provided board. */
  const resetBoard = useCallback(() => {
    setState(initialData ?? createInitialBirdEyeBoardState());
  }, [initialData]);

  const counts = useMemo<BirdEyeBoardCounts>(
    () => ({
      arriving: state.arriving.length,
      waiting: state.waiting.length,
      walking: state.walking.length,
      seated: state.ops.filter((o) => o.seated).length,
      opTotal: state.ops.length,
    }),
    [state],
  );

  return { state, movePatient, resetBoard, counts };
}
